import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { Formik, Form, Field } from "formik";
import { createCircleValidation } from "../../utility/validation/createCircleValidation";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CardMedia,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import { MultiSelectWithLimit, PayOutDayMultiSelect } from "./CreateCircle";
import { buttonColors, colors } from "../../utility/types/constant";
import {
  SUPPORTED_COUNTRIES,
  getCountryByCode,
} from "../../utility/stripeConnectCountries";

interface CircleFormProps {
  userData?: any;
  initialValues: any;
  handleSelectFile: any;
  imagePreview: any;
  handleSubmit: any;
  subTitle: string;
  title: string;
  isLoading?: boolean;
}

const CircleForm = (props: CircleFormProps) => {
  const {
    initialValues,
    handleSelectFile,
    imagePreview,
    handleSubmit,
    subTitle,
    title,
    isLoading,
  } = props;

  const dayOptions = [
    { label: "Monday", value: "Monday" },
    { label: "Tuesday", value: "Tuesday" },
    { label: "Wednesday", value: "Wednesday" },
    { label: "Thursday", value: "Thursday" },
    { label: "Friday", value: "Friday" },
    { label: "Saturday", value: "Saturday" },
    { label: "Sunday", value: "Sunday" },
  ];

  const payOutDayOptions = [
    { label: "Monday", value: "Monday" },
    { label: "Tuesday", value: "Tuesday" },
    { label: "Wednesday", value: "Wednesday" },
    { label: "Thursday", value: "Thursday" },
    { label: "Friday", value: "Friday" },
  ];

  return (
    <Formik
      initialValues={initialValues}
      onSubmit={handleSubmit}
      validationSchema={createCircleValidation}
    >
      {({ values, errors, touched, handleChange, getFieldProps }) => (
        <Form>
          <Card>
            <CardHeader subheader={subTitle} title={title} />

            <CardContent>
              <Box sx={{ mt: 5 }}>
                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Circle Name"
                      name="circleName"
                      onChange={handleChange}
                      value={values?.circleName}
                      error={!!(errors?.circleName && touched?.circleName)}
                      // helperText={
                      //   errors.circleName &&
                      //   touched.circleName &&
                      //   errors.circleName
                      // }
                      helperText={
                        errors.circleName && touched.circleName
                          ? typeof errors.circleName === "string"
                            ? errors.circleName
                            : "Invalid input"
                          : ""
                      }
                      sx={{
                        fontSize: "1.2rem",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "black",
                          },
                          "&:hover fieldset": {
                            borderColor: "black",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "black",
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl
                      sx={{
                        width: "100%",
                        fontSize: "1.2rem",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "black",
                          },
                          "&:hover fieldset": {
                            borderColor: "black",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "black",
                          },
                        },
                      }}
                    >
                      <InputLabel id="membersCount-label">
                        Number of Members
                      </InputLabel>
                      <Select
                        labelId="membersCount-label"
                        id="membersCount-label-helper"
                        value={values.membersCount}
                        error={
                          !!(errors?.membersCount && touched?.membersCount)
                        }
                        label="Intended Number of Members"
                        onChange={handleChange}
                        name="membersCount"
                      >
                        <MenuItem value="<=5">Less than or equal to 5</MenuItem>
                        <MenuItem value=">5 <10">
                          Greater than 5 and less than 10
                        </MenuItem>
                        <MenuItem value=">=10">
                          Greater than or equal to 10
                        </MenuItem>
                      </Select>
                      {/* <FormHelperText>
                          {errors.membersCount &&
                            touched.membersCount &&
                            errors.membersCount}
                        </FormHelperText> */}
                      <FormHelperText>
                        {errors.membersCount && touched.membersCount
                          ? typeof errors.membersCount === "string"
                            ? errors.membersCount
                            : "Invalid input"
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl
                      sx={{
                        width: "100%",
                        fontSize: "1.2rem",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "black",
                          },
                          "&:hover fieldset": {
                            borderColor: "black",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "black",
                          },
                        },
                      }}
                    >
                      <InputLabel id="country-label">
                        Circle Country
                      </InputLabel>
                      <Select
                        labelId="country-label"
                        id="country-label-helper"
                        value={values?.configuration?.country ?? "US"}
                        label="Circle Country"
                        onChange={(e) => {
                          const countryCode = e.target.value;
                          handleChange(e);
                          const config = getCountryByCode(countryCode);
                          if (config) {
                            // Auto-set currency from country
                            const syntheticEvent = {
                              target: {
                                name: "configuration.currency",
                                value: config.currency,
                              },
                            };
                            handleChange(syntheticEvent as any);
                          }
                        }}
                        name="configuration.country"
                      >
                        {[...SUPPORTED_COUNTRIES].sort((a, b) => a.name.localeCompare(b.name)).map((country) => (
                          <MenuItem key={country.code} value={country.code}>
                            {country.name}
                          </MenuItem>
                        ))}
                      </Select>
                      <FormHelperText>
                        Only countries supported by our payment provider are available. Currency is auto-set based on country.
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2, height: "100%" }}>
                      <Chip
                        label={`Currency: ${(values?.configuration?.currency ?? "usd").toUpperCase()}`}
                        color="primary"
                        variant="outlined"
                        sx={{ fontSize: "1rem", padding: "4px 8px" }}
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Contribution Amount"
                      name="configuration.contributionAmount"
                      error={
                        !!(
                          errors?.contributionAmount &&
                          touched?.contributionAmount
                        )
                      }
                      onChange={handleChange}
                      // value={values?.contributionAmount || ""}
                      value={values?.configuration.contributionAmount || ""}
                      sx={{
                        fontSize: "1.2rem",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "black",
                          },
                          "&:hover fieldset": {
                            borderColor: "black",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "black",
                          },
                        },
                      }}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl
                      sx={{
                        width: "100%",
                        fontSize: "1.2rem",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "black",
                          },
                          "&:hover fieldset": {
                            borderColor: "black",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "black",
                          },
                        },
                      }}
                    >
                      <InputLabel id="ContributionPeriod-label">
                        Contribution Period
                      </InputLabel>
                      <Select
                        labelId="contributionPeriod-label"
                        id="contributionPeriod-label-helper"
                        value={values?.configuration?.contributionPeriod ?? ""}
                        error={
                          !!(
                            errors?.contributionPeriod &&
                            touched.contributionPeriod
                          )
                        }
                        label="Contribution Period"
                        onChange={handleChange}
                        name="configuration.contributionPeriod"
                      >
                        <MenuItem value="Weekly">Weekly</MenuItem>
                        <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem>
                      </Select>
                      {/* <FormHelperText>
                          {errors.contributionPeriod &&
                            touched.contributionPeriod}
                        </FormHelperText> */}
                      <FormHelperText>
                        {errors.contributionPeriod && touched.contributionPeriod
                          ? typeof errors.contributionPeriod === "string"
                            ? errors.contributionPeriod
                            : "Invalid input"
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      component={MultiSelectWithLimit}
                      name="configuration.contributionDays"
                      options={dayOptions}
                      label="Contribution Days"
                    // value={values?.contribution[0]?.contributionDays}
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <FormControl
                      sx={{
                        width: "100%",
                        fontSize: "1.2rem",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "black",
                          },
                          "&:hover fieldset": {
                            borderColor: "black",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "black",
                          },
                        },
                      }}
                    >
                      <InputLabel id="PayOutPeriod-label">
                        PayOut Period
                      </InputLabel>
                      <Select
                        labelId="payOutPeriod-label"
                        id="payOutPeriod-label-helper"
                        value={values?.configuration.payOutPeriod ?? "Monthly"}
                        error={!!(errors?.payOutPeriod && touched.payOutPeriod)}
                        label="PayOut Period"
                        onChange={handleChange}
                        name="configuration.payOutPeriod"
                      >
                        {/* <MenuItem value="Weekly">Weekly</MenuItem> */}
                        {/* <MenuItem value="Bi-Weekly">Bi-Weekly</MenuItem> */}
                        <MenuItem value="Monthly">Monthly</MenuItem>
                        {/* <MenuItem value="Quarterly">Quarterly</MenuItem> */}
                      </Select>
                      {/* <FormHelperText>
                          {errors.payOutPeriod && touched.payOutPeriod}
                        </FormHelperText> */}
                      <FormHelperText>
                        {errors.payOutPeriod && touched.payOutPeriod
                          ? typeof errors.payOutPeriod === "string"
                            ? errors.payOutPeriod
                            : "Invalid input"
                          : ""}
                      </FormHelperText>
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <Field
                      component={PayOutDayMultiSelect}
                      value={values?.configuration.payOutDay}
                      name="configuration.payOutDay"
                      options={payOutDayOptions}
                      label="PayOut Days"
                    />
                  </Grid>

                  <Grid item xs={12} md={6} sx={{}}>
                    <input
                      style={{ display: "none" }}
                      id="upload-photo"
                      type="file"
                      name="upload-photo"
                      accept="image/*"
                      onChange={handleSelectFile}
                    />
                    <label htmlFor="upload-photo">
                      <Button
                        sx={{
                          // mt:5,
                          color: colors.white,
                          backgroundColor: buttonColors.primary,
                          "&:hover": {
                            backgroundColor: buttonColors.primary,
                          },
                        }}
                        size="large"
                        variant="contained"
                        component="span"
                      >
                        Add Circle Profile Image
                      </Button>
                    </label>
                  </Grid>

                  <Grid item container sx={{ mt: 4, justifyContent: "center" }}>
                    <Box
                      display="flex"
                      justifyContent="center"
                      alignItems="center"
                    >
                      {imagePreview && (
                        <Card sx={{ maxWidth: "100%" }}>
                          <CardMedia
                            sx={{ height: 250, width: 450 }}
                            image={imagePreview}
                            title="circle profile image"
                          />
                        </Card>
                      )}
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={12}>
                    <TextField
                      fullWidth
                      label="Circle Description"
                      name="description"
                      multiline
                      rows={5}
                      onChange={handleChange}
                      value={values.description}
                      error={!!(errors?.description && touched?.description)}
                      // helperText={
                      //   errors.description &&
                      //   touched.description &&
                      //   errors.description
                      // }
                      helperText={
                        errors.description && touched.description
                          ? typeof errors.description === "string"
                            ? errors.description
                            : "Invalid input"
                          : ""
                      }
                      sx={{
                        fontSize: "1.2rem",
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": {
                            borderColor: "black",
                          },
                          "&:hover fieldset": {
                            borderColor: "black",
                          },
                          "&.Mui-focused fieldset": {
                            borderColor: "black",
                          },
                        },
                      }}
                    />
                  </Grid>
                </Grid>
              </Box>
            </CardContent>

            <Divider sx={{ backgroundColor: "black" }} />

            <CardActions sx={{ justifyContent: "center" }}>
              <Button
                // disabled={isLoading}
                variant="contained"
                type="submit"
                sx={{
                  color: colors.white,
                  backgroundColor: colors.mainSecondary,
                  "&:hover": {
                    backgroundColor: colors.mainSecondary,
                  },
                  width: 390,
                  padding: 1,
                  fontSize: 18,
                }}
              >
                {isLoading ? <CircularProgress size={24} /> : title}
              </Button>
            </CardActions>
          </Card>
        </Form>
      )}
    </Formik>
  );
};

CircleForm.propTypes = {
  userData: PropTypes.any,
  initialValues: PropTypes.any,
  handleSelectFile: PropTypes.any,
  imagePreview: PropTypes.any,
  handleSubmit: PropTypes.any,
  subTitle: PropTypes.string,
  title: PropTypes.string,
  isLoading: PropTypes.bool,
};

export default CircleForm;
